'use client';

import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ArrowRight, CheckCircleRounded } from '@mui/icons-material';
import { Box, ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import { CascadingHoverMenus, CascadingMenuItem } from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, translate } from '@prismo-io/router';
import { LanguageCode, type LanguageCodeT, UserAccessType, type UserAccessTypeT } from '@prismo-io/schemas';
import { useFragment } from '@prismo-io/utils/client/use-fragment';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type FC, useMemo } from 'react';
type Props = {
  userUrl: string;
  access: UserAccessTypeT;
  order?: number;
  isCompleted?: boolean;
  disabled?: boolean;
};
export const AuraItem: FC<Props> = (props: Props) => {
  const {
    userUrl,
    isCompleted = false,
    access,
    order = 2,
    disabled = false
  } = props;
  const {
    i18n
  } = useLingui();
  const {
    locale = LanguageCode.Enum.fr
  } = i18n;
  const me = useMe();
  const isMe = me && me.url === userUrl;
  const pathname = usePathname();
  const fragment = useFragment();
  const baseUrl = useMemo(() => translate({
    app: Application.APP,
    path: '/profil/:user/comportement-aura',
    params: {
      user: userUrl
    },
    locale: locale as LanguageCodeT
  }), [locale, userUrl]);
  const selected = useMemo(() => {
    return pathname.startsWith(baseUrl);
  }, [baseUrl, pathname]);
  return <CascadingHoverMenus variant="popover" cascadingMenuProps={{
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    }
  }} button={props => <MenuItem {...props} href={baseUrl} component={Link} selected={selected} sx={{
    order,
    paddingY: 2
  }} disabled={!isCompleted && !isMe || disabled}>
          <ListItemIcon sx={{
      minWidth: 'auto'
    }}>
            {isCompleted && <CheckCircleRounded color="primary" fontSize="small" />}
            {!isCompleted && <Box component="div" borderRadius={9999} width="24px" height="24px" display="flex" alignItems="center" justifyItems="center" color="primary.main" sx={{
        borderStyle: 'solid',
        borderWidth: '2px',
        borderColor: 'primary.main'
      }}>
                <Typography sx={{
          flex: 1
        }} color="primary" fontSize="small" fontWeight="bolder" textAlign="center">
                  {order}
                </Typography>
              </Box>}
          </ListItemIcon>
          <ListItemText sx={{
      flex: 1
    }}>
            <Typography display="inline" fontWeight="bolder">
              Aura |{' '}
            </Typography>
            <Typography display="inline">
              <Trans>Comportement</Trans>
            </Typography>
          </ListItemText>
          <ListItemIcon sx={{
      minWidth: '12px !important'
    }}>
            <ArrowRight fontSize="small" />
          </ListItemIcon>
        </MenuItem>} popupId="aura-menu" data-sentry-element="CascadingHoverMenus" data-sentry-component="AuraItem" data-sentry-source-file="aura-item.tsx">
      <CascadingMenuItem component={Link} href={`${baseUrl}#aura-1`} selected={'#aura-1' === fragment} disabled={!isCompleted} data-sentry-element="CascadingMenuItem" data-sentry-source-file="aura-item.tsx">
        <Trans data-sentry-element="Trans" data-sentry-source-file="aura-item.tsx">Profil comportemental</Trans>
      </CascadingMenuItem>

      {access !== UserAccessType.Enum.VISITOR && access !== UserAccessType.Enum.FREE && <>
            <CascadingMenuItem component={Link} href={`${baseUrl}#aura-2`} selected={'#aura-2' === fragment} disabled={!isCompleted}>
              <Trans>Compétences comportementales</Trans>
            </CascadingMenuItem>

            <CascadingMenuItem component={Link} href={`${baseUrl}#aura-3`} selected={'#aura-3' === fragment} disabled={!isCompleted}>
              <Trans>Caractéristiques spécifiques</Trans>
            </CascadingMenuItem>
          </>}

      {access !== UserAccessType.Enum.VISITOR && access !== UserAccessType.Enum.FREE && access !== UserAccessType.Enum.PARTNER && <CascadingMenuItem component={Link} href={`${baseUrl}#aura-4`} disabled={!isCompleted}>
            <Trans>Comportement au sein d'un groupe</Trans>
          </CascadingMenuItem>}
    </CascadingHoverMenus>;
};